import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://storyshort.ai/pricing";
const REFUND = "https://storyshort.ai/refund";
const HOME = "https://storyshort.ai";
const TOOLS = "https://storyshort.ai/tools";
const CHECKED = "2026-10-08";

export const STORYSHORT: CompetitorRecord = {
  slug: "storyshort",
  name: "StoryShort",
  aliases: ["StoryShort.ai", "Story Short", "storyshort.ai"],
  url: HOME,
  pricingUrl: PRICING,
  category: "faceless",
  positioning:
    "AI faceless video generator that makes short-form videos people actually finish, with consistent characters, series automation and auto-posting.",
  plans: [
    {
      name: "Starter",
      monthly: 39,
      annualPerMonth: 23,
      annualTotal: 279,
      metered: "400 credits per month; a 30-second image video is about 15 credits, a 60-second one about 25",
      includes: [
        "1 series",
        "About 26 videos of 30 seconds or 16 of 60 seconds a month",
        "No watermark",
        "Long-form videos not in plan",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Growth",
      monthly: 69,
      annualPerMonth: 42,
      annualTotal: 499,
      metered: "1,200 credits per month, same per-video credit costs",
      includes: [
        "2 series",
        "About 80 videos of 30 seconds or 48 of 60 seconds a month",
        "No watermark",
        "Long-form videos not in plan",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Influencer",
      monthly: 129,
      annualPerMonth: 75,
      annualTotal: 899,
      metered: "2,400 credits per month; long-form videos cost 85 to 150 credits each",
      includes: [
        "3 series",
        "About 160 videos of 30 seconds or 96 of 60 seconds a month",
        "Long-form videos of 8 to 10 minutes",
        "No watermark",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Ultra",
      monthly: 199,
      annualPerMonth: 117,
      annualTotal: 1399,
      metered: "5,000 credits per month; long-form videos cost 85 to 150 credits each",
      includes: [
        "4 series",
        "About 330 videos of 30 seconds or 200 of 60 seconds a month",
        "Long-form videos of 8 to 10 minutes",
        "No watermark",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
  ],
  entryPlanName: "Starter",
  entryPlanIncludes:
    "400 credits and 1 series, about 26 videos of 30 seconds or 16 of 60 seconds in the cheapest image mode",
  freeTier: "Signing up is free, but credits require a paid plan; no free credit amount is published",
  trial: "None published; the refund page says only that if a free trial is offered you are not charged during it",
  meteringUnit:
    "Credits per generated video, from about 15 for a 30-second image video to 270+ for an avatar video; premium models cost more",
  exportResolution: "Not published on the pricing page or homepage",
  features: {
    longToShort: { v: "partial", note: "Long-video-to-shorts tool among 56 tools" },
    momentRanking: { v: "partial", note: "Finds best moments; no ranking shown" },
    reframing: { v: "n/a", note: "Not described on the site" },
    maxSource: { v: "n/a", note: "No source cap published" },
    layouts: { v: "n/a", note: "Characters and styles, not layouts" },
    redditStory: { v: "yes", note: "Reddit post to video tool" },
    fakeText: { v: "no", note: "None among the 56 tools" },
    aiImages: { v: "yes", note: "Consistent characters, premium video models" },
    voiceover: { v: "yes", note: "Preset voices, count not published" },
    voiceCloning: { v: "yes", note: "Clone from a short recording" },
    avatars: { v: "yes", note: "Talking avatar, 270+ credits each" },
    timelineEditor: { v: "no", note: "Chat-based shot regeneration instead" },
    captionStyles: { v: "n/a", note: "Saved presets, count not published" },
    captionLanguages: { v: "n/a", note: "Count not published" },
    bRoll: { v: "n/a", note: "Not described on the site" },
    fourK: { v: "n/a", note: "Resolution not published" },
    scheduling: { v: "yes", note: "TikTok, YouTube, Instagram" },
    analytics: { v: "partial", note: "Trend tracking, not your own stats" },
    socialTracker: { v: "yes", note: "Follows creators, flags outperforming videos" },
    api: { v: "yes", note: "API docs and MCP linked in the footer" },
    webhooks: { v: "n/a", note: "Not on the site" },
    web: { v: "yes" },
    desktop: { v: "no", note: "Not advertised" },
    mobile: { v: "no", note: "Not advertised" },
    teamSeats: { v: "n/a", note: "Not on the pricing page" },
    freeTier: { v: "partial", note: "Free sign-up, credits need a paid plan" },
    trial: { v: "n/a", note: "Refund page says only if offered" },
    metering: { v: "text", note: "Credits per video, about 15 to 418" },
    rollover: { v: "yes", note: "Up to 2x the monthly amount" },
    refunds: { v: "text", note: "Non-refundable; 15-day exception at zero credits" },
    cancelInApp: { v: "n/a", note: "Mechanism not stated on their pages" },
  },
  billingTerms: {
    refunds:
      "Subscriptions and credit purchases are non-refundable. A discretionary refund is possible within 15 days if zero credits were used. Billing errors and charges after cancellation are always refunded. Unused credits roll over up to 2x the monthly amount.",
    cancel:
      "The refund page says charges after cancellation are refunded, but neither it nor the pricing page describes how to cancel.",
    sourceUrl: REFUND,
    checkedAt: CHECKED,
  },
  quotes: [],
  theirCrayoPages: [
    {
      url: "https://storyshort.ai/alternatives/crayo",
      claims: [
        "Publishing is still on you: download the video and post it to each platform yourself",
        "Crayo marked No for built-in scheduling calendar, native publishing to TikTok, YouTube Shorts and Reels, and series that generate videos daily",
        "Crayo listed as having free sign-up and no watermark on paid plans",
        "Crayo marked Yes for text-prompt short generation, AI script writing, AI voiceover, auto captions and faceless story videos",
        "Long-form: Shorts focused",
        "If you just want quick clips, Crayo does that well",
        "The table omits AutoClip clipping, the timeline editor, the public API, View Tracker, Enhance Speech and the free tools",
      ],
    },
  ],
  verification: {
    method: "fetch",
    date: CHECKED,
    note: `Pricing page, refund page, homepage, tools index and about page fetched directly on Oct 8, 2026. Monthly prices, yearly per-month prices, yearly totals, credit allowances, series limits and the stated video counts come from ${PRICING}. Per-video credit costs come from the pricing page's credit table; the 418-credit shot regeneration figure comes from a homepage demo and applies to a premium video model. Refund and rollover terms come from ${REFUND}. Export resolution, caption language count, voice count and team seats are not published anywhere fetched. Nothing needs a browser check.`,
  },
  seoPattern: "storyshort.ai/alternatives/<competitor-slug>, including a page about Crayo, plus 56 /tools pages",
};
